// client/src/components/Newsletter.jsx
import React, { useState } from 'react';
import { Mail, CheckCircle, AlertCircle, Loader2, Zap } from 'lucide-react';

const Newsletter = ({ className = "", variant = "default" }) => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState({
        isSubmitting: false,
        isSubmitted: false,
        error: null
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email.trim()) {
            setStatus({ isSubmitting: false, isSubmitted: false, error: 'Email is required' });
            return;
        }

        setStatus({ isSubmitting: true, isSubmitted: false, error: null });

        try {
            const response = await fetch('/api/newsletter/subscribe', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email.trim(),
                    preferences: {
                        techUpdates: true,
                        projectNews: true,
                        industryInsights: true
                    }
                }),
            });

            const result = await response.json();

            if (response.ok && result.success) {
                setStatus({ isSubmitting: false, isSubmitted: true, error: null });
                setEmail('');
            } else {
                throw new Error(result.message || 'Failed to subscribe');
            }
        } catch (error) {
            setStatus({
                isSubmitting: false,
                isSubmitted: false,
                error: error.message || 'Something went wrong. Please try again.'
            });
        }
    };

    if (variant === "footer") {
        return (
            <div className={`${className}`}>
                <div className="flex items-center space-x-2 mb-4">
                    <Zap className="h-6 w-6 text-blue-400" />
                    <h3 className="text-lg font-medium text-white">Innovation Updates</h3>
                </div>
                <p className="text-gray-300 text-sm mb-4">
                    Get insights on systematic innovation, breakthrough solutions, and the intersection of ancient wisdom with modern technology.
                </p>

                {status.isSubmitted ? (
                    <div className="flex items-center text-blue-400 text-sm">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Thank you for subscribing to our innovation updates!
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-3">
                        <div>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                disabled={status.isSubmitting}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={status.isSubmitting}
                            className="w-full inline-flex justify-center items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            {status.isSubmitting ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Subscribing...
                                </>
                            ) : (
                                <>
                                    <Mail className="h-4 w-4 mr-2" />
                                    Subscribe
                                </>
                            )}
                        </button>
                        {status.error && (
                            <p className="text-red-400 text-sm flex items-center">
                                <AlertCircle className="h-4 w-4 mr-1" />
                                {status.error}
                            </p>
                        )}
                    </form>
                )}
            </div>
        );
    }

    // Default inline variant
    return (
        <div className={`bg-blue-50 border border-blue-200 rounded-lg p-6 ${className}`}>
            <div className="flex items-center mb-4">
                <Zap className="h-6 w-6 text-blue-600 mr-3" />
                <h3 className="text-lg font-medium text-gray-900">Innovation Updates</h3>
            </div>

            <p className="text-gray-600 mb-4">
                Stay informed on systematic innovation breakthroughs, technology insights, and how ancient wisdom shapes modern solutions from Baraqu LLC.
            </p>

            {status.isSubmitted ? (
                <div className="flex items-center text-blue-600">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    <span className="font-medium">Thank you for subscribing!</span>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email address"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                            disabled={status.isSubmitting}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={status.isSubmitting}
                        className="inline-flex justify-center items-center px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                        {status.isSubmitting ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Subscribing...
                            </>
                        ) : (
                            'Subscribe'
                        )}
                    </button>
                </form>
            )}

            {status.error && (
                <p className="mt-3 text-red-600 text-sm flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {status.error}
                </p>
            )}
        </div>
    );
};

export default Newsletter;